import React from "react";
import { Link } from "react-router-dom";
import { CULTURE_TOPICS, QUICK_FACTS, HERO_QUOTES } from "@/data/culture";
import {
  Landmark, Mountain, Drum, UtensilsCrossed, Palette, Music, ArrowUpRight,
} from "lucide-react";

const ICONS = { Landmark, Mountain, Drum, UtensilsCrossed, Palette, Music };

export default function Explore() {
  const quote = HERO_QUOTES[Math.floor(Math.random() * HERO_QUOTES.length)];

  return (
    <div className="p-6 sm:p-10 max-w-7xl mx-auto">
      {/* Hero */}
      <section className="reveal">
        <div className="flex items-center gap-3">
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#ff4500] font-mono">
            ── Esplore Ayiti
          </div>
          {/* Haitian flag stripes */}
          <div className="flex h-3 w-10 overflow-hidden">
            <div className="flex-1 bg-[#00209F]" />
            <div className="flex-1 bg-[#D21034]" />
          </div>
        </div>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-3 tracking-tighter">
          Haiti. <span className="text-[#ff4500]">Devan.</span>
        </h1>
        <p className="text-[#a3a3a3] mt-4 text-sm sm:text-base max-w-2xl leading-relaxed">
          The story we own. From the Revolution of 1804 to the rhythms of Kanaval today — a living
          archive of Haitian history, geography, art, food and music. Share, upload, contribute.
        </p>
        <div className="mt-6 inline-block border-l-2 border-[#ff4500] pl-4">
          <div className="font-display text-xl italic">"{quote.kreyol}"</div>
          <div className="text-xs text-[#a3a3a3] mt-1 uppercase tracking-[0.2em]">
            {quote.english}
          </div>
        </div>
      </section>

      {/* Quick facts */}
      <section className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {QUICK_FACTS.map((f) => (
          <div key={f.label} className="bg-[#141414] border border-[#2a2a2a] p-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">
              {f.label}
            </div>
            <div className="font-display text-base mt-1">{f.value}</div>
          </div>
        ))}
      </section>

      {/* Topics grid */}
      <section className="mt-14">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Discover</h2>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3] font-mono">
            // {CULTURE_TOPICS.length} topics
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {CULTURE_TOPICS.map((t, i) => {
            const Icon = ICONS[t.icon] || Landmark;
            return (
              <Link
                key={t.slug}
                to={`/app/explore/${t.slug}`}
                data-testid={`topic-${t.slug}`}
                className={`group bg-[#141414] border border-[#2a2a2a] hover:border-[#ff4500]/60 transition-all duration-300 overflow-hidden reveal reveal-${(i % 4) + 1}`}
              >
                <div className="relative aspect-[16/9] overflow-hidden bg-[#1a1a1a]">
                  <img
                    src={t.image}
                    alt={t.title}
                    className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-500"
                  />
                  <div
                    className="absolute inset-0 mix-blend-multiply"
                    style={{ background: `linear-gradient(180deg, transparent 30%, ${t.accent}55 100%)` }}
                  />
                  <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-sm px-2 py-1 flex items-center gap-1.5">
                    <Icon className="w-3.5 h-3.5" style={{ color: t.accent }} />
                    <span className="text-[10px] uppercase tracking-[0.25em] font-mono">
                      {t.titleKreyol}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-display text-2xl tracking-tight">{t.title}</h3>
                    <ArrowUpRight className="w-5 h-5 text-[#a3a3a3] group-hover:text-[#ff4500] transition" />
                  </div>
                  <p className="text-sm text-[#a3a3a3] mt-2 line-clamp-2">{t.blurb}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Call to contribute */}
      <section className="mt-16 bg-[#141414] border border-[#2a2a2a] p-8 sm:p-12">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.35em] text-[#ff4500] font-mono">
              ── Kontribye
            </div>
            <h2 className="font-display text-3xl sm:text-4xl mt-3 tracking-tighter">
              Have something to share?
            </h2>
            <p className="text-[#a3a3a3] mt-3 text-sm leading-relaxed">
              Upload a video of your grandmother making soup joumou. A song by a
              kompa band from your hometown. A photo from Carnival in Jacmel.
              Every upload helps the world see Haiti the way we know it.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <Link to="/app/videos" data-testid="explore-upload-video" className="inline-flex items-center gap-2 px-6 py-3 bg-[#ff4500] hover:bg-[#ff5722] text-white text-sm">
              Upload a video <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/app/music" data-testid="explore-upload-music" className="inline-flex items-center gap-2 px-6 py-3 bg-[#1a1a1a] border border-[#2a2a2a] hover:border-[#ff4500] text-sm">
              Upload music <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/app/profile" data-testid="explore-promote" className="inline-flex items-center gap-2 px-6 py-3 bg-[#1a1a1a] border border-[#2a2a2a] hover:border-[#ff4500] text-sm">
              Promote my page <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
